
import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Navigation from "@/components/Navigation";
import { Button } from "@/components/ui/button";
import { useInvestmentData } from "@/hooks/useInvestmentData";
import BidSubmissionDialog from "@/components/investor/BidSubmissionDialog";
import BalloonPaymentAlert from "@/components/investor/BalloonPaymentAlert";
import InvestmentDetailsHeader from "@/components/investor/InvestmentDetailsHeader";
import KeyMetricsCards from "@/components/investor/KeyMetricsCards";
import OriginalContractCard from "@/components/investor/OriginalContractCard";
import CurrentStatusCard from "@/components/investor/CurrentStatusCard";
import InvestmentSummaryCard from "@/components/investor/InvestmentSummaryCard";
import InvestmentActionButtons from "@/components/investor/InvestmentActionButtons";

const InvestmentDetails = () => {
  const { id } = useParams();
  const { investments, isLoading } = useInvestmentData();
  const [isBidDialogOpen, setIsBidDialogOpen] = useState(false);
  
  const investment = investments.find(inv => inv.id === id);

  const handleSubmitBid = () => {
    setIsBidDialogOpen(true);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
        <Navigation />
        <div className="py-8 px-4">
          <div className="max-w-4xl mx-auto">
            <div className="animate-pulse">
              <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
              <div className="h-64 bg-gray-200 rounded"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!investment) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
        <Navigation />
        <div className="py-8 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Investment Not Found</h1>
            <p className="text-gray-600 mb-6">The investment opportunity you're looking for doesn't exist.</p>
            <Link to="/investor-portal">
              <Button>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Opportunities
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
        <Navigation />
        
        <div className="py-8 px-4">
          <div className="max-w-4xl mx-auto">
            <InvestmentDetailsHeader investment={investment} />
            
            <KeyMetricsCards investment={investment} />

            {/* Main Content Grid */}
            <div className="grid lg:grid-cols-2 gap-8">
              <OriginalContractCard investment={investment} />
              <CurrentStatusCard investment={investment} />
            </div>

            <BalloonPaymentAlert investment={investment} />
            
            <InvestmentSummaryCard investment={investment} />

            <InvestmentActionButtons 
              investment={investment} 
              onSubmitBid={handleSubmitBid} 
            />
          </div>
        </div>
      </div>
      
      <BidSubmissionDialog 
        isOpen={isBidDialogOpen}
        onClose={() => setIsBidDialogOpen(false)}
        investmentId={investment?.id || ""}
      />
    </>
  );
};

export default InvestmentDetails;
